import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Settings2, X } from "lucide-react";
import { useGetIdentity, useGetList } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Group } from "../types";

type Membership = { group_id: number; groups: Group };

export const ProspectGroupsEdit = ({ prospectId }: { prospectId: number }) => {
  const queryClient = useQueryClient();
  const { identity } = useGetIdentity();
  const queryKey = ["prospect_groups", prospectId];

  const { data: memberships = [] } = useQuery({
    queryKey,
    queryFn: async () => {
      const { data, error } = await getSupabaseClient()
        .from("prospect_groups")
        .select("group_id, groups(id, name, color)")
        .eq("prospect_id", prospectId);
      if (error) throw error;
      return (data ?? []) as unknown as Membership[];
    },
  });

  const { data: allGroups = [] } = useGetList<Group>("groups", {
    pagination: { page: 1, perPage: 100 },
    sort: { field: "name", order: "ASC" },
  });

  const addGroup = useMutation({
    mutationFn: async (groupId: number) => {
      const { error } = await getSupabaseClient()
        .from("prospect_groups")
        .insert({
          prospect_id: prospectId,
          group_id: groupId,
          sales_id: identity?.id,
        });
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const removeGroup = useMutation({
    mutationFn: async (groupId: number) => {
      const { error } = await getSupabaseClient()
        .from("prospect_groups")
        .delete()
        .eq("prospect_id", prospectId)
        .eq("group_id", groupId);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey }),
  });

  const selectedIds = new Set(memberships.map((m) => m.group_id));
  const unselected = allGroups.filter((g) => !selectedIds.has(Number(g.id)));

  return (
    <div className="flex flex-wrap items-center gap-2">
      {memberships.map((m) =>
        m.groups ? (
          <Badge
            key={m.group_id}
            variant="secondary"
            className="text-black font-normal gap-1"
            style={{ backgroundColor: m.groups.color }}
          >
            {m.groups.name}
            <button
              type="button"
              aria-label="Remove from list"
              onClick={() => removeGroup.mutate(m.group_id)}
              className="hover:opacity-70"
            >
              <X className="size-3" />
            </button>
          </Badge>
        ) : null,
      )}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-6">
            <Plus className="size-3 mr-1" /> List
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          {unselected.map((g) => (
            <DropdownMenuItem
              key={g.id}
              onClick={() => addGroup.mutate(Number(g.id))}
            >
              <Badge
                variant="secondary"
                className="text-black font-normal"
                style={{ backgroundColor: g.color }}
              >
                {g.name}
              </Badge>
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem asChild>
            <Link to="/groups" className="flex items-center gap-2">
              <Settings2 className="size-3" /> Manage lists
            </Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
